import { createAsyncThunk } from "@reduxjs/toolkit"

import { httpGet, httpPost } from "@/lib/http"
import { serializedError } from "@/store/helper"
import { QuestionFeedback } from "@/types"

/**
 * The triage queue of question reports raised by students.
 *
 * The API is admin-only and returns every report newest first, optionally
 * narrowed to one status. Filtering by status server side rather than in the
 * page keeps the payload small once there is a long history of resolved ones.
 */
export const fetchQuestionFeedback = createAsyncThunk(
  "questionFeedback/fetchQueue",
  async (status: string | undefined, { rejectWithValue, dispatch }) => {
    try {
      const query = status ? `?status=${encodeURIComponent(status)}` : ""
      const res = await httpGet({ url: `question-feedback/queue${query}`, dispatch })
      return (res?.data || []) as QuestionFeedback[]
    } catch (err: any) {
      return rejectWithValue(serializedError(err))
    }
  }
)

export const updateQuestionFeedbackStatus = createAsyncThunk(
  "questionFeedback/updateStatus",
  async ({ id, status }: { id: string; status: string }, { rejectWithValue, dispatch }) => {
    try {
      const res = await httpPost({
        url: `question-feedback/${id}/status?status=${encodeURIComponent(status)}`,
        payload: {},
        dispatch,
      })

      // httpPost swallows failures and returns null, so an unchanged report has
      // to be treated as a failure here or the list would silently show a state
      // the server never accepted.
      if (!res?.data) {
        return rejectWithValue({ message: "Could not update this report" })
      }

      return res.data as QuestionFeedback
    } catch (err: any) {
      return rejectWithValue(serializedError(err))
    }
  }
)
